import { EmptyState, ExclamationmarkIcon, Input, SendIcon } from '@bp/ui-components';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ActivityProtocol.module.scss';
import { MatrixEvent } from 'matrix-js-sdk';
import { Activity } from '../Activity/Activity';
import { showErrorToast } from '../../utils/showErrorToast';
import { useActivityProtocol } from '../../hooks/matrix/useActivityProtocol';
import { useMatrixRoomMessages } from '../../hooks/matrix/useMatrixRoomMessages';
import { useGetMatrixRoomByName } from '../../hooks/matrix/useGetMatrixRoomByName';
import { NotificationReason, RoomNames } from '../../utils/matrixClient';
import { DocumentType, FragmentType, graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { uuidRegexHelper } from '../../utils/uuidRegexHelper';
import { PermissionCheckerFieldsDocument, usePermissionChecker } from '../../hooks/usePermissionChecker';
import { SubmissionStatus } from 'client/gql/graphql';

import { CourseAssignmentsStudentContentFragmentQuery, CurrentSubmissionPageQuery } from '../../client/gql/graphql';
import { CurrentSubmissionPageQueryDocument } from '../Submission/Submission';
import { CourseAssignmentStudentContentPageQueryDocument } from '../../pages/Courses/subpages/StudentContent/CourseAssignmentStudentContent';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';

export type CurrentSubmissionPageQuerySubmission = Pick<
  | DocumentType<typeof CurrentSubmissionPageQueryDocument>
  | DocumentType<typeof CourseAssignmentStudentContentPageQueryDocument>,
  'submissions'
>['submissions'][0];

type ActivityProtocolType = {
  submission: CurrentSubmissionPageQuerySubmission;
  newSubmissionContent: () => void;
  query: CurrentSubmissionPageQuery | CourseAssignmentsStudentContentFragmentQuery | undefined;
};

const UpdateSubmissionsDocument = graphql(`
  mutation BpUpdateSubmissionsInActivityProtocol($where: SubmissionWhere!, $update: SubmissionUpdateInput!) {
    updateSubmissions(where: $where, update: $update) {
      submissions {
        uuid
        __typename
        status
      }
    }
  }
`);

const ActivityProtocolQueryDocument = graphql(`
  query ActivityData($profiles: [ID!]!) {
    profiles(where: { uuid_IN: $profiles }) {
      uuid
      __typename
      firstName
      lastName
    }
  }
`);

export const ActivityProtocol: FC<ActivityProtocolType> = ({ query, submission, newSubmissionContent }) => {
  const { t } = useTranslation();

  const submissionUuid = submission?.uuid ?? '';

  const currentAssignment = submission?.assignment[0];
  const groupSubject = {
    uuid: currentAssignment?.holder?.group?.uuid ?? '0',
    organization: { uuid: currentAssignment?.holder?.group.organization?.uuid ?? '0' },
  };

  const permissionChecker = usePermissionChecker(query as FragmentType<typeof PermissionCheckerFieldsDocument>); // me lazy

  const isTeacher = permissionChecker?.canViewSubmissions({
    uuid: currentAssignment?.uuid ?? '',
    ownerUuid: submission?.uuid ?? '',
    groupUuid: groupSubject.uuid,
    organization: groupSubject.organization,
  });

  const { isAvailable: matrixAvailable } = useMatrixClient();
  const { getRoom } = useGetMatrixRoomByName();
  const room = getRoom(submissionUuid, RoomNames.Submission);
  const roomMembers = room?.getMembers();

  const profileUuids = roomMembers?.map((m) => {
    const result = uuidRegexHelper('user', m.userId);
    return result ? result[1] : '';
  });

  const [{ data: roomMembersData }] = useQuery({
    query: ActivityProtocolQueryDocument,
    variables: { profiles: profileUuids ?? [] },
  });

  const messages = useMatrixRoomMessages(room);

  const { sendMessage, redactMessage } = useActivityProtocol();
  const [, updateSubmissionMutation] = useMutation(UpdateSubmissionsDocument);

  const [comment, setComment] = useState('');

  const redactErrorToast = (resp: Response) => {
    showErrorToast({
      name: 'Error',
      message: resp.statusText,
      graphQLErrors: [],
    });
  };

  const grantFeedback = async (eventId: string) => {
    if (submissionUuid !== '' && room) {
      await updateSubmissionMutation({
        where: {
          uuid: submissionUuid,
        },
        update: {
          status: SubmissionStatus.Feedback,
        },
      }).catch((err) => showErrorToast(err));

      // Matrix Stuff
      const resp = await redactMessage(room.roomId, eventId);
      if (!resp.ok) {
        redactErrorToast(resp);
      }

      if (resp.ok) {
        await sendMessage(submissionUuid, NotificationReason.SubmissionFeedback);
      }
    }
  };

  const rejectFeedback = async (eventId: string) => {
    if (submissionUuid && room) {
      // Matrix Stuff
      const resp = await redactMessage(room.roomId, eventId);
      !resp.ok
        ? redactErrorToast(resp)
        : await sendMessage(submissionUuid, NotificationReason.SubmissionFeedbackRejected);
    }
  };

  const publishSubmission = async (isPublic: boolean, eventId: string) => {
    if (submissionUuid !== '' && room) {
      const updateResp = await updateSubmissionMutation({
        where: {
          uuid: submissionUuid,
        },
        update: {
          public: isPublic,
        },
      }).catch((err) => {
        showErrorToast(err);
      });

      // Matrix Stuff
      if (updateResp) {
        const resp = await redactMessage(room.roomId, eventId);
        !resp.ok
          ? redactErrorToast(resp)
          : await sendMessage(
              submissionUuid,
              isPublic ? NotificationReason.SubmissionPublished : NotificationReason.SubmissionDePublished,
              null,
              true,
            );
      }
    }
  };

  const sendComment = async () => {
    if (room && comment) {
      await sendMessage(submissionUuid, NotificationReason.SubmissionComment, comment);
    }
    setComment('');
  };

  return (
    <div className={styles['activity-protocol']}>
      {!matrixAvailable ? (
        <EmptyState title={t('activityProtocol.noAvailable')} icon={<ExclamationmarkIcon />} fitParent />
      ) : (
        messages.map((event: MatrixEvent) => (
          <Activity
            key={event.getId()}
            message={event}
            roomMembersData={roomMembersData}
            grantFeedback={grantFeedback}
            rejectFeedback={rejectFeedback}
            publishSubmission={publishSubmission}
            newSubmissionContent={newSubmissionContent}
            isTeacher={isTeacher ?? false}
          />
        ))
      )}
      <Input
        name={'comment'}
        placeholder={t('submissions.askQuestion')}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === 'Enter') await sendComment();
        }}
        value={comment}
        suffix={
          <div className={styles['input-icon']} onClick={sendComment}>
            <SendIcon />
          </div>
        }
      />
    </div>
  );
};
